import React, { useCallback, useRef, useState } from "react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { ADMIN_GUIDE_GROUPS, ADMIN_GUIDE_SECTIONS, AdminGuideSectionId } from "./adminGuideSections";
import { GuideNavContext } from "./primitives";

const AdminGuide: React.FC = () => {
  const [activeId, setActiveId] = useState<AdminGuideSectionId>(ADMIN_GUIDE_SECTIONS[0].id);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const index = Math.max(
    0,
    ADMIN_GUIDE_SECTIONS.findIndex((section) => section.id === activeId)
  );
  const active = ADMIN_GUIDE_SECTIONS[index];
  const previous = ADMIN_GUIDE_SECTIONS[index - 1];
  const next = ADMIN_GUIDE_SECTIONS[index + 1];
  const group = ADMIN_GUIDE_GROUPS.find((g) => g.id === active.group);
  const Body = active.body;

  // Links inside a long section sit far below its heading: bring the new heading into view.
  const goTo = useCallback((id: AdminGuideSectionId) => {
    setActiveId(id);
    const heading = headingRef.current;
    if (heading && heading.getBoundingClientRect().top < 0) {
      heading.scrollIntoView({ block: "start" });
    }
  }, []);

  return (
    <GuideNavContext.Provider value={goTo}>
      <div className="flex flex-col gap-6 lg:flex-row">
        <nav aria-label="Admin guide sections" className="w-full shrink-0 lg:w-72 lg:self-start">
          {ADMIN_GUIDE_GROUPS.map((g) => (
            <div key={g.id} className="mb-4 last:mb-0" data-guide-group={g.id}>
              <p
                id={`admin-guide-group-${g.id}`}
                className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400"
              >
                {g.label}
              </p>
              <ul className="space-y-0.5" aria-labelledby={`admin-guide-group-${g.id}`}>
                {ADMIN_GUIDE_SECTIONS.filter((section) => section.group === g.id).map((section) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => goTo(section.id)}
                      aria-current={active.id === section.id ? "page" : undefined}
                      className={`flex w-full items-start gap-3 rounded-md px-3 py-2 text-left transition ${
                        active.id === section.id
                          ? "bg-red-50 text-red-700 ring-1 ring-inset ring-red-200"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <section.icon className="mt-0.5 shrink-0" aria-hidden="true" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{section.label}</span>
                        <span className="block text-xs text-gray-500">{section.description}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <article className="min-w-0 flex-1 text-sm leading-7 text-gray-700" aria-labelledby="admin-guide-heading">
          {group && (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{group.label}</p>
          )}
          <h2 id="admin-guide-heading" ref={headingRef} className="scroll-mt-4 text-xl font-semibold text-gray-800">
            {active.label}
          </h2>
          <p className="mb-6 text-gray-500">{active.description}</p>
          <Body />

          <div className="mt-10 flex flex-wrap justify-between gap-4 border-t border-gray-200 pt-4">
            {previous ? (
              <button
                type="button"
                onClick={() => goTo(previous.id)}
                className="inline-flex items-center gap-2 font-medium text-gray-700 hover:underline"
              >
                <FiArrowLeft aria-hidden="true" />
                {previous.label}
              </button>
            ) : (
              <span />
            )}
            {next && (
              <button
                type="button"
                onClick={() => goTo(next.id)}
                className="inline-flex items-center gap-2 font-medium text-red-600 hover:underline"
              >
                Next: {next.label}
                <FiArrowRight aria-hidden="true" />
              </button>
            )}
          </div>
        </article>
      </div>
    </GuideNavContext.Provider>
  );
};

export default AdminGuide;
